/**
 * Pure gating + copy for the start-page import-discovery card ("Found 14
 * connections in DBeaver, SSMS and 2 more apps"). Kept dependency-free so the
 * rules that decide whether the card shows — and what it claims — are unit
 * tested without React, Electron, or a persisted store.
 */
import type { ImportSourceInfo } from '../../../types/connectionImport';

/**
 * The card is an offer, not a nag: it appears only when this machine actually
 * has importable connections, and never again once dismissed.
 *
 * Counting arrives after detection, so the two "no number" cases are kept
 * apart. Counts not back yet (or failed) → still show, with the count-less
 * headline: a detected store is good evidence. Counts back and totalling zero →
 * hide, because the tool is installed but holds nothing Jam can import, and the
 * card would otherwise lead to a menu of zeroes.
 */
export function shouldShowImportDiscoveryCard(input: {
  detectedCount: number;
  dismissed: boolean;
  countsLoaded: boolean;
  totalCount: number;
}): boolean {
  if (input.dismissed || input.detectedCount === 0) return false;
  return !input.countsLoaded || input.totalCount > 0;
}

/**
 * Trim a source's disambiguating parenthetical for headline use — the menu
 * still shows the full label ('VS Code (SQL extensions)' → 'VS Code').
 */
export function shortSourceLabel(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim() || label;
}

/** 'DBeaver' · 'DBeaver and SSMS' · 'DBeaver, SSMS and 2 more apps'. */
export function joinSourceLabels(labels: string[]): string {
  const named = labels.slice(0, 2);
  const rest = labels.length - named.length;
  if (rest > 0) return `${named.join(', ')} and ${rest} more app${rest === 1 ? '' : 's'}`;
  return named.join(' and ');
}

/** How many direct source buttons the card renders before folding into the menu. */
export const MAX_CARD_SOURCE_BUTTONS = 3;

/**
 * The sources the card offers as DIRECT one-click buttons. Field data drove
 * this shape: the generic "Import connections" menu trigger converted almost
 * nothing (60 sessions shown → ~2 menu opens → 0 imports in 14d), so the card
 * now puts the concrete choices on itself. Ordered by importable-connection
 * count (registry order as tiebreak/fallback); sources that counted to zero
 * are dropped once counts are in — a button that opens a review dialog of
 * nothing converts worse than no button.
 */
export function pickCardSources(
  sources: ImportSourceInfo[],
  counts: Partial<Record<ImportSourceInfo['id'], number>>,
  countsLoaded: boolean,
  max: number = MAX_CARD_SOURCE_BUTTONS
): ImportSourceInfo[] {
  const eligible = countsLoaded ? sources.filter((s) => (counts[s.id] ?? 0) > 0) : sources;
  return eligible
    .map((source, index) => ({ source, index }))
    .sort((a, b) => (counts[b.source.id] ?? 0) - (counts[a.source.id] ?? 0) || a.index - b.index)
    .slice(0, max)
    .map((entry) => entry.source);
}

/**
 * The card headline. Counting needs a scan, so it lands after detection does —
 * until then (or when every store parsed empty) the headline stays honest and
 * simply names the apps.
 */
export function buildImportDiscoveryHeadline(sources: ImportSourceInfo[], totalCount: number): string {
  if (sources.length === 0) return '';
  const where = joinSourceLabels(sources.map((s) => shortSourceLabel(s.label)));
  if (totalCount <= 0) return `Found saved connections in ${where}`;
  return `Found ${totalCount} connection${totalCount === 1 ? '' : 's'} in ${where}`;
}
